import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'
import { useMemoizedFn, useThrottleFn } from 'ahooks'

interface Option {
  onDragStart?: () => void
  onDragChange: ([x, y]: [number, number]) => void
  onDragEnd?: () => void
}

export function useMouseDrag({ onDragStart, onDragChange, onDragEnd }: Option) {
  const xRef = useRef(-1)
  const yRef = useRef(-1)
  const isDragging = useRef(false)

  const handleMove = useMemoizedFn(() => {
    const globalMouseMove = (event: any) => {
      event.preventDefault()
      isDragging.current = true
      const nowX = event.clientX
      const nowY = event.clientY
      onDragChange([nowX - xRef.current, nowY - yRef.current])
    }

    const globalMouseUp = () => {
      document.removeEventListener('mousemove', globalMouseMove)
      document.removeEventListener('mouseup', globalMouseUp)
      if (!isDragging.current) return
      setTimeout(() => {
        isDragging.current = false
        onDragEnd?.()
      }, 100)
    }

    document.addEventListener('mouseup', globalMouseUp)
    document.addEventListener('mousemove', globalMouseMove)
  })

  const handleMouseDown = useMemoizedFn((event: any) => {
    event.preventDefault()
    event.stopPropagation()
    xRef.current = event.clientX
    yRef.current = event.clientY
    handleMove()
    onDragStart?.()
  })

  return handleMouseDown
}

export function useResize<T extends HTMLElement>(
  dom: RefObject<T>,
  onResize: () => void,
  gutter: number = 16,
) {
  const { run: throttleCall } = useThrottleFn(onResize, { wait: gutter })

  useEffect(() => {
    if (!dom.current) return

    throttleCall()
    const observe = new ResizeObserver(throttleCall)
    observe.observe(dom.current)

    return () => {
      observe.disconnect()
    }
  }, [!!dom.current, throttleCall])
}
